import { cn } from '@bridebook/aura';
import {
  type PlannerFaceLook,
  PlannerFace,
} from '@bridebook/web/components/ai-planner/planner-face';
import {
  AI_PLANNER_RING_CONIC_GRADIENT,
  AI_PLANNER_RING_GRADIENT,
} from '@bridebook/web/lib/ai-planner/constants';
import { m, type TargetAndTransition } from 'motion/react';
import styles from './planner-fab.module.css';

/**
 * Attention loops the button can play while the planner is closed, one at a time.
 * `nudge` hops with a badge, `halo` sends rings outwards, `breathe` grows and shrinks.
 * The spinning ring and the face's look can play with any of them.
 */
export type PlannerFabIdle = 'nudge' | 'halo' | 'breathe';

const BREATHE: TargetAndTransition = {
  scale: [1, 1.06, 1],
  transition: { duration: 2.9, ease: 'easeInOut', repeat: Infinity },
};

// Takes the button back to rest when a loop stops
const REST: TargetAndTransition = { scale: 1 };

const getWrapAnimation = (idle?: PlannerFabIdle) => (idle === 'breathe' ? BREATHE : REST);

// Count of news in the planner, pinned to the top-right corner
const PlannerBadge = ({ count }: { count: number }) => (
  <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-[#ff766a] px-1 text-[11px] font-semibold text-white">
    {count}
  </span>
);

/**
 * A slowly rotating conic gradient, so the ring seems to flow. It sits in a clipped ring,
 * so it must be large enough to cover the corners. Uses CSS, not JS, because it plays on
 * every page. Stops for reduced motion.
 */
export const PlannerRingSpin = ({ className }: { className?: string }) => (
  <span
    aria-hidden
    className={cn(
      'absolute top-1/2 left-1/2 -translate-1/2',
      'motion-safe:animate-spin',
      className,
    )}
    style={{ background: AI_PLANNER_RING_CONIC_GRADIENT, animationDuration: '4s' }}
  />
);

// Ring drawn over the gradient ring that grows outwards and fades
const OutwardRing = ({ animate }: { animate: TargetAndTransition }) => (
  <m.span
    aria-hidden
    className="pointer-events-none absolute -inset-[3px] rounded-full border-3 border-[#9462ff]"
    animate={animate}
  />
);

/**
 * Two rings that grow outwards one after the other. Uses CSS and stops for reduced motion.
 * Round by default; the class sets the shape for other buttons.
 */
export const PlannerPing = ({ className = 'rounded-full' }: { className?: string }) => (
  <>
    {[styles.ping, cn(styles.ping, styles.pingLate)].map((ring) => (
      <span
        key={ring}
        aria-hidden
        className={cn(
          'pointer-events-none absolute -inset-[3px] border-3 border-[#9462ff] opacity-0',
          ring,
          className,
        )}
      />
    ))}
  </>
);

type PlannerFabProps = {
  // Turns the gradient round the ring
  isSpinning?: boolean;
  // How the face moves; still when not set
  look?: PlannerFaceLook;
  // Attention loop to play; none leaves the button still
  idle?: PlannerFabIdle;
  // Count shown on the corner badge with the `nudge` loop
  badge?: number;
  // Sends a single ring outwards, played right after the planner closes
  isPulsing?: boolean;
  // Sends two rings outwards, played at an interval
  isPinging?: boolean;
  className?: string;
};

/**
 * The round entry button's content: the planner's face inside the gradient ring.
 * It only renders the looks and animations; the parent button decides when to play them.
 * Needs a LazyMotion parent.
 */
export const PlannerFab = ({
  isSpinning = false,
  look,
  idle,
  badge,
  isPulsing = false,
  isPinging = false,
  className,
}: PlannerFabProps) => (
  <m.span
    className={cn('relative block size-12', idle === 'nudge' && styles.nudge, className)}
    animate={getWrapAnimation(idle)}>
    {/* Drawn first, so the rings pass under the button */}
    {isPinging && <PlannerPing />}
    {/* Gradient ring, 3px wide, with the face on a soft fill inside it */}
    <span
      className="absolute inset-0 overflow-hidden rounded-full shadow-[0_5px_15px_rgba(0,0,0,0.05)]"
      style={{ background: AI_PLANNER_RING_GRADIENT }}>
      {isSpinning && <PlannerRingSpin className="size-[90px]" />}
    </span>
    <span className="absolute inset-[3px] flex items-center justify-center rounded-full bg-indigo-crush-04">
      <PlannerFace size={32} look={look} />
    </span>
    {idle === 'halo' && (
      <OutwardRing
        animate={{
          scale: [0.94, 1.5, 1.5],
          opacity: [0.5, 0, 0],
          transition: { duration: 2.4, ease: 'easeOut', times: [0, 0.7, 1], repeat: Infinity },
        }}
      />
    )}
    {isPulsing && (
      <OutwardRing
        animate={{
          scale: [0.8, 1.85],
          opacity: [0.9, 0],
          transition: { duration: 0.68, ease: [0.2, 0.7, 0.3, 1] },
        }}
      />
    )}
    {idle === 'nudge' && !!badge && <PlannerBadge count={badge} />}
  </m.span>
);
